// History — the built-in workspace app that shows the workspace activity log
// (the same `workspaceProject.auditLog` read /admin/logs makes, scoped to the
// active workspace) as a horizontal timeline: one station per day, newest
// first, and under each station the people who were active that day with
// their events. This file owns the read and every shaping function the
// components in components/HistoryTimeline.js call through `el.call`; the
// components hold no logic of their own.
//
// Every export is a `function` declaration (Rule 8): `el.call` invokes it
// with `this` = the calling element, which is how a function reaches the
// session language (`localizedDate`, `polyglot`), the roster (`findPerson`)
// and the router. A function that takes `root` accepts the root state or any
// state that carries `.root`, so a component may pass `s.root` or `s` alike.
import _wsShared from '@symbo-ls/workspace-shared'
import { activeWorkspaceId } from './scopedClient.js'

const _historyRootOf = (root) => (root && root.root ? root.root : root)

const _call = (host, name, ...args) =>
  host && typeof host.call === 'function' ? host.call(name, ...args) : undefined

// The day a row belongs to. On a clock (historyClock): the LOCAL date of its
// timestamp in the clock's zone — the server scores `occurred_on` as the UTC
// date, which files a Tbilisi evening after 20:00 under the day before.
// Without a clock, or without a readable timestamp: the audit log's own date
// column, else the day of the timestamp (the key /admin/logs groups by).
export function historyDayKey(row, clock) {
  if (!row) return ''
  const at = clock ? clock.wall(row.created_at) : null
  if (at) return at.date
  return row.occurred_on || (row.created_at ? String(row.created_at).slice(0, 10) : '')
}

// ── The working day ──────────────────────────────────────────────────────

// The workspace's working-time calendar: `settings.workingTime` on the full
// workspace record the shell holds in `root.workspace` (the workspace read
// returns the whole settings bag to every member) — { timezone (IANA),
// days (ISO weekdays, 1 = Monday … 7 = Sunday), start 'HH:MM', end 'HH:MM'
// ('24:00' = midnight), holidays ['YYYY-MM-DD'] }. The server's contract
// (core/utils/workingTime.js normalizeWorkingTime): unset or invalid is
// plain calendar time — null here. Normalized the same way: days unique and
// sorted, holidays the well-formed dates.
export function historyWorkingTime(root) {
  const r = _historyRootOf(root)
  const settings = r && r.workspace && r.workspace.settings
  const raw = settings ? settings.workingTime : null
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const tz = raw.timezone
  if (typeof tz !== 'string' || !tz) return null
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(0)
  } catch (_) {
    return null
  }
  const minutes = (hhmm, midnight) => {
    if (midnight && hhmm === '24:00') return 24 * 60
    const m = typeof hhmm === 'string' ? /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm) : null
    return m ? Number(m[1]) * 60 + Number(m[2]) : null
  }
  const start = minutes(raw.start, false)
  const end = minutes(raw.end, true)
  if (start === null || end === null || end <= start) return null
  const days = Array.isArray(raw.days) ? raw.days : []
  if (!days.length || days.some((d) => !Number.isInteger(d) || d < 1 || d > 7)) return null
  const holidays = Array.isArray(raw.holidays)
    ? raw.holidays.filter((h) => typeof h === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(h))
    : []
  return {
    timezone: tz,
    days: [...new Set(days)].sort((a, b) => a - b),
    start: raw.start,
    end: raw.end,
    holidays: [...new Set(holidays)].sort()
  }
}

// The clock the History page reads a day by. With a calendar: its zone, its
// working hours and the middle of them — the axis between the day's first
// half (above) and its second (below); Mon–Fri 10:00–19:00 splits at 14:30.
// Without one: the browser's own zone and the whole day, split at noon
// (plain calendar time — every day works). Minutes of the local day; the
// split is whole minutes, so a card's clock time and a half's range agree to
// the minute. `wall(ts)` reads an instant's local date and minute on this
// clock — `{ date: 'YYYY-MM-DD', minutes }`, or null for no readable instant
// — through ONE formatter per clock, since a render reads every row.
export function historyClock(root) {
  const cal = historyWorkingTime(root)
  const minutes = (hhmm) =>
    hhmm === '24:00' ? 24 * 60 : Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))
  const start = cal ? minutes(cal.start) : 0
  const end = cal ? minutes(cal.end) : 24 * 60
  const timeZone = cal ? cal.timezone : undefined
  let fmt = null
  const wall = (ts) => {
    const t = ts instanceof Date ? ts.getTime() : ts ? new Date(ts).getTime() : NaN
    if (!Number.isFinite(t)) return null
    if (!fmt) {
      fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    const p = {}
    for (const part of fmt.formatToParts(new Date(t))) p[part.type] = part.value
    return {
      date: `${p.year}-${p.month}-${p.day}`,
      minutes: (Number(p.hour) % 24) * 60 + Number(p.minute)
    }
  }
  return {
    calendar: !!cal,
    timeZone,
    start,
    end,
    split: Math.floor((start + end) / 2),
    days: cal ? cal.days : [1, 2, 3, 4, 5, 6, 7],
    holidays: cal ? cal.holidays : [],
    wall
  }
}

// The half of the day a row belongs to on `clock`: 'first' before the axis,
// 'second' at or after it — the hours outside the working day included (an
// early start reads with the morning, a late evening with the afternoon).
// A row without a readable timestamp sits in the first half.
export function historyHalfOf(row, clock) {
  const c = clock || historyClock(null)
  const at = row ? c.wall(row.created_at) : null
  return at && at.minutes >= c.split ? 'second' : 'first'
}

// Whether a day (a 'YYYY-MM-DD' local date) is a working day on `clock`: one
// of the calendar's weekdays and not one of its holidays. Plain calendar
// time works every day.
export function historyWorkday(dayKey, clock) {
  const c = clock || historyClock(null)
  if (!c.calendar) return true
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey || '')
  if (!m) return true
  const dow = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay()
  return c.days.includes(dow === 0 ? 7 : dow) && !c.holidays.includes(dayKey)
}

// The minutes a half covers on `clock`: the first half from the day's
// opening to the axis, the second from the axis to its close.
export function historyHalfBounds(clock, half) {
  const c = clock || historyClock(null)
  return half === 'second' ? { from: c.split, to: c.end } : { from: c.start, to: c.split }
}

// ── The read ─────────────────────────────────────────────────────────────

// THE AUDIT LOG IS PATH-SCOPED PER WORKSPACE (/workspaces/:id/activity-log),
// and the SDK refuses a scopeless read before it leaves the client. On a cold
// deep link the shell has no active workspace yet, so the read waits for the
// shell's own readiness signal (shared/functions/activeWorkspaceRecord.js),
// the way /admin/logs and /admin/usage do. The listener is this function
// itself — a stable identity, so the Set-based subscription never stacks.
//
// THE READ FOLLOWS THE WORKSPACE, NOT THE PAGE. The page reads on mount, and a
// same-route workspace switch never re-creates it — while the switch wipes the
// History slice back to "loading" (state-app.js). So the shell's switch tail
// (loadPrivateData.js, onWorkspaceChange) calls this again once the new record
// is adopted, and three rules keep that read honest:
//   • The id is the shell's own resolver (`activeWorkspaceId`): the tab's
//     record by `id` — or by `_id` on a raw workspace document — BEFORE the
//     session claim, which lags a switch and still names the workspace just
//     left (measured live, root probe 14 s after a cross-org pick).
//   • A reply that outlived its workspace is dropped. Its rows describe a
//     tenant the user has left, and the wipe has already run, so nothing
//     would correct them.
//   • When a reply is dropped and the workspace in view has no rows yet, that
//     workspace is read now.
//
// A THIRD path a same-route switch never covers: a COLD BOOT that adopts a
// PROVISIONAL workspace, reads it here successfully (a real id, so the branch
// below never subscribes to the readiness signal), and only later self-
// corrects to the settled one while `loadPrivateData`'s sign-in fan-out is
// still in flight (auth.js's `_loadInFlight` guard skips the switch tail
// there on purpose — see its own comment). Without a listener, the rows never
// change: WS-COLD-BOOT-IDENTITY-CORRECTION-KEEPS-PROVISIONAL-WORKSPACE-ROWS-1
// (measured: another workspace's 193-event log painted under the settled
// workspace's header). `onWorkspaceIdentityCorrected` fires ONLY for that
// self-correction, never for a deliberate switch (which already re-runs this
// itself, above) — so listening unconditionally here can never double a
// switch's own read.
//
// `via` is the caller's own seam, `{ sdk, write }`: the switch tail hands its
// SDK and its epoch-guarded `put`, so a superseded switch drops its patch like
// every other PHASE-2 slice. As either readiness listener this function is
// called `(root, workspace)` — a workspace record carries neither key, so
// that call reads through the shell's SDK and writes straight to root.
export async function loadHistoryEvents(root, via) {
  const r = _historyRootOf(root)
  if (!r) return
  const seam = via && typeof via === 'object' ? via : {}
  const idNow = () => String(activeWorkspaceId(r) || '')
  _wsShared.functions.onWorkspaceIdentityCorrected(loadHistoryEvents)
  const wsId = idNow()
  if (!wsId) {
    _wsShared.functions.onActiveWorkspaceFull(loadHistoryEvents)
    return
  }
  const write = typeof seam.write === 'function' ? seam.write : (patch) => r.update(patch)
  const land = (patch) => {
    if (idNow() === wsId) {
      write(patch)
      return
    }
    if (idNow() && !r.historyLoaded) {
      loadHistoryEvents(r, seam.sdk ? { sdk: seam.sdk } : undefined)
    }
  }
  try {
    const sdk =
      seam.sdk && typeof seam.sdk.execute === 'function' ? seam.sdk : _wsShared.functions.getSDK()
    const result = await sdk.execute('workspaceProject.auditLog', 'list', {
      filter: {},
      options: {
        workspaceId: wsId,
        columns:
          'id, user_email, activity_type, score, source_table, source_id, metadata, created_at, occurred_on',
        // PostgREST-style "column.direction" — the one order shape the
        // audit-log worker parses (see pages/admin/logs.js).
        order: 'created_at.desc',
        limit: 400
      }
    })
    const rows = Array.isArray(result)
      ? result
      : (result && (result.data || result.rows || result.events)) || []
    land({ historyEvents: rows || [], historyLoaded: true, historyLoadError: false })
  } catch (err) {
    // A failed read is not an empty history: the page says the read failed.
    console.error('[history]', err)
    land({ historyEvents: [], historyLoaded: true, historyLoadError: true })
  }
}

// Is `href` the History page? The switch tail (loadPrivateData.js,
// `historySurfaceActive`) asks it of the URL path and of each pane's href — a
// pane href may carry a query. The shell keeps a route predicate next to the
// page it names, like `isUserProfilePath` for the profile surface.
export function isHistoryPath(href) {
  return String(href || '').split('?')[0] === '/history'
}

// ── Shaping ──────────────────────────────────────────────────────────────

// The rows the two filters leave: a person (their email) and an activity
// type. `over` names a filter explicitly (`{ person: null }` = everyone,
// whatever the state says) — the rail's "Everyone" and "All activity" counts.
export function historyRows(root, over) {
  const r = _historyRootOf(root)
  const all = Array.isArray(r && r.historyEvents) ? r.historyEvents : []
  const o = over || {}
  const person = 'person' in o ? o.person : (r && r.historyPerson) || null
  const type = 'type' in o ? o.type : (r && r.historyType) || null
  return all.filter(
    (row) =>
      row && (!person || (row.user_email || '') === person) && (!type || row.activity_type === type)
  )
}

// The timeline: days newest first. A day is the LOCAL day of the workspace's
// working-time calendar (historyClock) and splits at the middle of its
// working hours (Nika 2026-10-01: "make top part, first half of the day and
// bottom part second half of the day based on workdays"): `_firstHalf` holds
// the cards above the axis, `_secondHalf` the cards under it — one card per
// person per half, the person's events of that half newest first. Each half
// puts its cards NEAREST THE AXIS FIRST, so the column reads down the clock:
// the upper half (a reversed column) sets the person whose latest event is
// closest to the split next to the axis, the lower half the person whose
// earliest event is. `_groups` is the whole day per person, most recently
// active first — the station's count line reads the whole day. `_workday`
// says whether the calendar works that day, `_firstRange` / `_secondRange`
// are the minutes each half covers (the labels print them) and `_timeZone`
// is the clock's zone — the station reads today and yesterday on it, and
// every event carries it too, so a card tells its time on the clock its half
// was chosen by without reading the calendar again. Every level carries a
// `key` for keyed reconciliation, prefixed so no data value can name an HTML
// tag (a data key that equals a tag name BECOMES that tag); a half's card key
// names the half, so a person's two cards of one day open on their own.
export function historyDays(root) {
  const clock = historyClock(root)
  const at = (row) => new Date(row.created_at).getTime() || 0
  const days = new Map()
  for (const row of historyRows(root)) {
    const day = historyDayKey(row, clock)
    if (!days.has(day)) days.set(day, { first: [], second: [] })
    days.get(day)[historyHalfOf(row, clock)].push(row)
  }
  const groupsOf = (day, rows, half) => {
    const authors = new Map()
    for (const row of rows) {
      const email = row.user_email || ''
      if (!authors.has(email)) authors.set(email, [])
      authors.get(email).push(row)
    }
    return [...authors.entries()].map(([email, events]) => {
      const key = half ? `g-${day}-${half}-${email}` : `g-${day}-${email}`
      const sorted = events
        .slice()
        .sort((a, b) => at(b) - at(a))
        .map((e, i) => ({
          ...e,
          key: `ev-${e.id || `${day}-${email}-${i}`}`,
          _timeZone: clock.timeZone
        }))
      const group = {
        key,
        _groupKey: key,
        _day: day,
        _email: email,
        _count: sorted.length,
        _score: sorted.reduce((sum, e) => sum + (e.score || 0), 0),
        _latestAt: sorted.length ? at(sorted[0]) : 0,
        _earliestAt: sorted.length ? at(sorted[sorted.length - 1]) : 0,
        _events: sorted
      }
      if (half) group._half = half
      return group
    })
  }
  return [...days.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([day, halves]) => {
      const groups = groupsOf(day, [...halves.first, ...halves.second], null).sort(
        (a, b) => b._latestAt - a._latestAt
      )
      return {
        key: `d-${day}`,
        _day: day,
        _timeZone: clock.timeZone,
        _workday: historyWorkday(day, clock),
        _firstRange: historyHalfBounds(clock, 'first'),
        _secondRange: historyHalfBounds(clock, 'second'),
        _count: groups.reduce((sum, g) => sum + g._count, 0),
        _score: groups.reduce((sum, g) => sum + g._score, 0),
        _people: groups.length,
        _groups: groups,
        _firstHalf: groupsOf(day, halves.first, 'first').sort((a, b) => b._latestAt - a._latestAt),
        _secondHalf: groupsOf(day, halves.second, 'second').sort(
          (a, b) => a._earliestAt - b._earliestAt
        )
      }
    })
    .map(historyDensity)
}

// The soft range behind the axis (the reference: a faint mountain line of
// the activity's volume runs under the years): each day column paints its
// own segment as a clip-path polygon, the day's count as the peak at the
// column's middle and the two edge heights shared with its neighbours
// (their average), so the segments meet into one range. Heights are
// percentages of the range box, the busiest day at 90 % so the peak keeps
// headroom; a stage of one day is a flat hill.
export function historyDensity(day, i, days) {
  const max = Math.max(1, ...days.map((d) => d._count || 0))
  const v = (d) => (d ? (d._count || 0) / max : 0)
  const mid = v(day)
  const left = i > 0 ? (v(days[i - 1]) + mid) / 2 : mid
  const right = i < days.length - 1 ? (mid + v(days[i + 1])) / 2 : mid
  const y = (x) => 100 - Math.round(x * 90)
  return {
    ...day,
    _densityClip: `polygon(0% ${y(left)}%, 50% ${y(mid)}%, 100% ${y(right)}%, 100% 100%, 0% 100%)`
  }
}

// The events a person's card shows: the newest three, or all of them once
// the card is opened (`_historyExpanded[groupKey]`).
export function historyGroupEvents(state) {
  const PREVIEW = 3
  const s = state || {}
  const open = s.root && s.root._historyExpanded ? !!s.root._historyExpanded[s._groupKey] : false
  const events = Array.isArray(s._events) ? s._events : []
  return open ? events : events.slice(0, PREVIEW)
}

// The people of the log with their event counts, busiest first — counted
// under the type filter alone, so the person rows keep their numbers while a
// person is chosen.
export function historyPeople(root) {
  const r = _historyRootOf(root)
  const all = Array.isArray(r && r.historyEvents) ? r.historyEvents : []
  const type = (r && r.historyType) || null
  const counts = new Map()
  for (const row of all) {
    if (!row || (type && row.activity_type !== type)) continue
    const email = row.user_email || ''
    counts.set(email, (counts.get(email) || 0) + 1)
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([email, count]) => ({ key: `p-${email}`, _email: email, _count: count }))
}

// The activity types of the log with their counts, busiest first — counted
// under the person filter alone (the mirror of historyPeople).
export function historyTypes(root) {
  const r = _historyRootOf(root)
  const all = Array.isArray(r && r.historyEvents) ? r.historyEvents : []
  const person = (r && r.historyPerson) || null
  const counts = new Map()
  for (const row of all) {
    if (!row || (person && (row.user_email || '') !== person)) continue
    const type = row.activity_type || ''
    counts.set(type, (counts.get(type) || 0) + 1)
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([type, count]) => ({ key: `t-${type}`, _type: type, _count: count }))
}

// The header's one line: how many events, people and days the filters leave
// — the days counted on the stage's clock, so the number is the columns'.
export function historySummary(root) {
  const clock = historyClock(root)
  const rows = historyRows(root)
  const people = new Set()
  const days = new Set()
  for (const row of rows) {
    people.add(row.user_email || '')
    days.add(historyDayKey(row, clock))
  }
  return { events: rows.length, people: people.size, days: days.size }
}

// A count in the session language: `{count} events`, or its singular form at
// one. Both keys are LITERAL at every call site (docs/I18N-CONVENTION.md
// §1.3 — a key is never concatenated, so the bundle gate can see it).
export function historyCountText(key, keyOne, count) {
  const n = Number(count) || 0
  const k = n === 1 ? keyOne : key
  const text = _call(this, 'polyglot', k)
  return String(typeof text === 'string' && text ? text : k).replace('{count}', String(n))
}

// ── Labels ───────────────────────────────────────────────────────────────

// The bundle key of an activity type's label. An unknown type (an admin can
// add types in the scoring config) echoes its own name — polyglot returns a
// non-key verbatim.
export function historyTypeLabelKey(type) {
  const KEYS = {
    standup: 'workspace.history.type.standup',
    ticket_update: 'workspace.history.type.ticketUpdate',
    note_edit: 'workspace.history.type.noteEdit',
    kb_edit: 'workspace.history.type.kbEdit',
    meet_call: 'workspace.history.type.meetCall'
  }
  return KEYS[type] || type || ''
}

// The glyph of an activity type (brand sprite names).
export function historyTypeIcon(type) {
  const ICONS = {
    standup: 'discussion',
    ticket_update: 'check',
    note_edit: 'document',
    kb_edit: 'library',
    meet_call: 'video'
  }
  return ICONS[type] || 'sfHistory'
}

// The event's own words, or '' when it carries none — the ONE reader of them:
// the History card, the /admin/logs row (components/AdminChrome.js, through
// `el.call`) and the AI page context of /history (functions/pageContext.js)
// all read it, and each adds only its own last resort. A standup's words
// arrive as `summary_preview`: the activity scorer writes the first 120
// characters of the summary under that key, and nothing under title / summary
// / body. Private copies of a chain that stopped at `body` left every standup
// titled with its table name, "standup_activity", on /history and /admin/logs,
// and untitled in the AI page context.
export function historyEventWords(row) {
  const md = (row && row.metadata) || {}
  return md.title || md.summary || md.body || md.summary_preview || ''
}

// A card's title: the event's own words, else its table name (the History card
// and the /admin/logs row).
export function historyEventTitle(row) {
  return historyEventWords(row) || (row && row.source_table) || ''
}

// The short reference of an event's source: the ticket number when the log
// carries one, else the head of the source id. The /admin/logs row reads it
// too, through `el.call`.
export function historyEventRef(row) {
  const md = (row && row.metadata) || {}
  if (md.ticket_number) return `#${md.ticket_number}`
  if (row && row.source_id) return String(row.source_id).slice(0, 8)
  return ''
}

// Where the event happened. The /admin/logs row links through it too.
export function historyEventHref(row) {
  if (!row) return null
  const md = row.metadata || {}
  switch (row.activity_type) {
    case 'ticket_update':
      return md.ticket_number ? `/tasks/my?ticket=${md.ticket_number}` : '/tasks/my'
    case 'standup':
      return '/tasks/standups'
    case 'note_edit':
      return row.source_id ? `/notes?id=${row.source_id}` : '/notes'
    case 'kb_edit':
      return row.source_id ? `/notes/knowledge-base?id=${row.source_id}` : '/notes/knowledge-base'
    case 'meet_call':
      return row.source_id ? `/meet?room=${row.source_id}` : '/meet'
    default:
      return null
  }
}

// Opens the event where it happened. `this` is the card, or the /admin/logs
// reference link. A ticket the session already holds (root.myTickets) opens in
// the ticket modal in place, like the ticket cards elsewhere; everything else
// routes to the owning page.
export function openHistoryEvent(row) {
  const el = this
  if (!el || !row) return
  const root = el.state && el.state.root
  if (row.activity_type === 'ticket_update' && root) {
    const md = row.metadata || {}
    const ticket = (root.myTickets || []).find(
      (t) =>
        t.id === row.source_id ||
        (md.ticket_number && (t.number === md.ticket_number || t.id === md.ticket_number))
    )
    if (ticket) {
      root.update({ selectedTicket: { ...ticket } })
      return
    }
  }
  const href = historyEventHref(row)
  if (!href) return
  let host = el
  while (host && !host.routes) host = host.parent
  if (host) el.router(href, host)
}

// ── People ───────────────────────────────────────────────────────────────

export function historyPersonName(email) {
  const me = _call(this, 'findPerson', email)
  return (me && me.name) || email || ''
}

// A `background` value: the member's photo, else the generated initials
// avatar the roster rows use — never a bare colour disc.
export function historyPersonAvatar(email) {
  const me = _call(this, 'findPerson', email)
  const src = (me && me.avatar) || _call(this, 'avatarUrl', (me && me.name) || email || '?') || ''
  return src ? `url(${src}) center/cover` : 'transparent'
}

// ── Dates ────────────────────────────────────────────────────────────────

// A day's station on the axis, in the session language: the day number, the
// short month (and the year when it is not this year), the short weekday,
// and the word for today or yesterday — read on `timeZone` when the day was
// cut on one (historyDays' `_timeZone`, the calendar's zone), else on the
// browser's clock. Without an element the numbers stay and the names fall
// back to the browser's own.
export function historyStation(dayKey, timeZone) {
  // Noon: a date-only key parsed at midnight can slip a day across a time zone.
  const d = new Date(`${dayKey}T12:00:00`)
  if (!dayKey || Number.isNaN(d.getTime())) {
    return { day: '', month: '', year: '', weekday: '', relative: '' }
  }
  const word = (key) => {
    const text = _call(this, 'polyglot', key)
    return typeof text === 'string' && text && text !== key ? text : ''
  }
  const fmt = (opts) => {
    const out = _call(this, 'localizedDate', d, opts)
    return typeof out === 'string' && out ? out : d.toLocaleDateString(undefined, opts)
  }
  // Today's and yesterday's dates on the day's clock ('YYYY-MM-DD'); an
  // unreadable zone falls back to the browser's clock.
  let zoned = null
  if (timeZone) {
    try {
      const p = {}
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(new Date())
      for (const part of parts) p[part.type] = part.value
      const prev = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day) - 1))
      zoned = { today: `${p.year}-${p.month}-${p.day}`, yesterday: prev.toISOString().slice(0, 10) }
    } catch (_) {
      zoned = null
    }
  }
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  const isToday = zoned ? dayKey === zoned.today : d.toDateString() === today.toDateString()
  const isYesterday = zoned
    ? dayKey === zoned.yesterday
    : d.toDateString() === yesterday.toDateString()
  const relative = isToday
    ? word('common.time.today')
    : isYesterday
      ? word('common.time.yesterday')
      : ''
  return {
    day: String(d.getDate()),
    month: fmt({ month: 'short' }),
    year: d.getFullYear() === today.getFullYear() ? '' : String(d.getFullYear()),
    weekday: fmt({ weekday: 'short' }),
    relative
  }
}

// A clock time in the session language (24 h where the language reads one),
// on `timeZone` when the event carries one (historyDays stamps the
// calendar's zone on every event) — the clock the card's half was chosen by,
// so a 14:40 card never sits above the 14:30 axis for a reader in another
// zone — else on the browser's. A zone the runtime cannot read falls back to
// the browser's clock rather than blanking the card.
export function historyTime(ts, timeZone) {
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const bare = { hour: 'numeric', minute: '2-digit' }
  const zoned = timeZone ? { ...bare, timeZone } : bare
  const read = (opts) => {
    const out = _call(this, 'localizedDate', d, opts)
    return typeof out === 'string' && out ? out : d.toLocaleTimeString(undefined, opts)
  }
  try {
    return read(zoned)
  } catch (_) {
    return read(bare)
  }
}

// A half's range on the session clock — `range` is the minutes it covers
// (historyDays' `_firstRange` / `_secondRange`): '10:00–14:30' and
// '14:30–19:00' for Mon–Fri 10:00–19:00, '00:00–12:00' and '12:00–00:00'
// without a calendar. Wall-clock times, not instants: each is formatted as a
// UTC date in UTC, so no zone and no daylight-saving shift can move it.
export function historyHalfRange(range) {
  if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to)) return ''
  const opts = { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }
  const clock = (min) => {
    const d = new Date(Date.UTC(2000, 0, 1, Math.floor(min / 60), min % 60))
    const out = _call(this, 'localizedDate', d, opts)
    return typeof out === 'string' && out ? out : d.toLocaleTimeString(undefined, opts)
  }
  return `${clock(range.from)}–${clock(range.to)}`
}

// ── Interaction ──────────────────────────────────────────────────────────

// The filters toggle: choosing the chosen person or type again clears it.
export function setHistoryPerson(email) {
  const root = this && this.state && this.state.root
  if (!root) return
  const next = email && root.historyPerson !== email ? email : null
  root.update({ historyPerson: next })
}

export function setHistoryType(type) {
  const root = this && this.state && this.state.root
  if (!root) return
  const next = type && root.historyType !== type ? type : null
  root.update({ historyType: next })
}

export function toggleHistoryGroup(groupKey) {
  const root = this && this.state && this.state.root
  if (!root || !groupKey) return
  const open = { ...(root._historyExpanded || {}) }
  if (open[groupKey]) delete open[groupKey]
  else open[groupKey] = true
  root.update({ _historyExpanded: open })
}

// Steps the stage one day column sideways. `this` is the caller: the stage
// itself (the arrow keys) or a header button (found through the page's
// AppMain).
export function scrollHistoryStage(direction) {
  const el = this
  if (!el) return
  let stage = el.key === 'HistoryStage' ? el : null
  if (!stage && typeof el.lookup === 'function') {
    const main = el.lookup('AppMain')
    stage = main && typeof main.lookdown === 'function' ? main.lookdown('HistoryStage') : null
  }
  const node = stage && stage.node
  if (!node || typeof node.scrollBy !== 'function') return
  const first = node.firstElementChild
  const column = first ? first.getBoundingClientRect().width : 0
  const step = column || node.clientWidth * 0.8
  node.scrollBy({ left: (direction < 0 ? -1 : 1) * step, behavior: 'smooth' })
}

// A vertical wheel over a stage that has nothing to scroll down moves it
// sideways through the days; a stage taller than its box keeps the wheel
// vertical, and a sideways gesture is the browser's own. A half of a day
// column that can scroll (`data-history-scroll`, more cards than its box)
// keeps the wheel for itself: the pointer over it scrolls the cards, not
// the days.
export function panHistoryStage(event) {
  const node = this && this.node
  if (!node || !event) return
  if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
  let t = event.target
  while (t && t !== node) {
    if (t.dataset && t.dataset.historyScroll && t.scrollHeight > t.clientHeight + 1) return
    t = t.parentElement
  }
  if (node.scrollHeight > node.clientHeight + 1) return
  if (node.scrollWidth <= node.clientWidth + 1) return
  node.scrollLeft += event.deltaY
}
